
import { useState, useEffect, useCallback, memo } from "react";
import { CSVLink } from "react-csv";
import { useNavigate } from "react-router-dom";
import AnimacionCarga from "../animacion_de_carga";
import TablaGenerica from "../tablas";
import BarraSuperior from "../barra_superior";
import { crearRegistro, actualizarRegistro, eliminarRegistro } from "../../../functions/crud";
import {
  RUTA_USUARIOS, CORREO_SOPORTE, cargarUsuariosTickets, inicialesDe,
  Modal, Campo, useConfirmacion, useAviso,
} from "./comunes";
import "bootstrap-icons/font/bootstrap-icons.css";
import "../../css/tickets.css";

const COLUMNAS = [
  { label: "Nombre completo", key: "Nombre_Completo" },
  { label: "Correo", key: "Usuario" },
  { label: "Cargo", key: "Cargo" },
  { label: "Proceso", key: "Proceso" },
];

const VACIO = { Nombre_Completo: "", Usuario: "", Cargo: "", Proceso: "" };


const ModalUsuario = ({ usuario, alGuardar, alCerrar }) => {
  const [datos, setDatos] = useState({ ...VACIO, ...(usuario || {}) });
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");

  const cambiar = (campo) => (e) => setDatos((d) => ({ ...d, [campo]: e.target.value }));

  const enviar = async () => {
    if (!datos.Nombre_Completo.trim()) return setError("El nombre completo es obligatorio.");
    if (!datos.Usuario.trim()) return setError("El correo es obligatorio.");

    setError("");
    setGuardando(true);
    try {
      await alGuardar({
        Nombre_Completo: datos.Nombre_Completo.trim(),
        Usuario: datos.Usuario.trim(),
        Cargo: datos.Cargo.trim(),
        Proceso: datos.Proceso.trim(),
      });
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal
      titulo={usuario ? "Editar usuario" : "Nuevo usuario de tickets"}
      subtitulo="Estos usuarios se pueden asignar como solicitantes de un ticket"
      ancho="md"
      alCerrar={alCerrar}
      pie={
        <>
          {error && <span style={{ color: "#b3261e", fontSize: "0.8rem" }}>{error}</span>}
          <div className="tk-derecha">
            <button type="button" className="tk-btn" onClick={alCerrar} disabled={guardando}>Cancelar</button>
            <button type="button" className="tk-btn tk-btn-primario" onClick={enviar} disabled={guardando}>
              {guardando ? "Guardando…" : usuario ? "Guardar" : "Crear"}
            </button>
          </div>
        </>
      }
    >
      <div className="tk-rejilla">
        <Campo etiqueta="Nombre completo" anchoTotal>
          <input type="text" autoFocus value={datos.Nombre_Completo} onChange={cambiar("Nombre_Completo")} placeholder="Nombre y apellido" />
        </Campo>

        <Campo etiqueta="Correo" ayuda="Con este correo el usuario ve sus propios tickets." anchoTotal>
          <input type="text" value={datos.Usuario} onChange={cambiar("Usuario")} placeholder="correo@dominio.com" />
        </Campo>

        <Campo etiqueta="Cargo">
          <input type="text" value={datos.Cargo} onChange={cambiar("Cargo")} placeholder="Ej. Ejecutivo de Cuenta" />
        </Campo>

        <Campo etiqueta="Proceso">
          <input type="text" value={datos.Proceso} onChange={cambiar("Proceso")} placeholder="Área o proceso" />
        </Campo>
      </div>
    </Modal>
  );
};


const ListadoUsuariosTickets = memo(() => {
  const navegar = useNavigate();

  const [datos, setDatos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [modal, setModal] = useState(null);

  const { elemento: confirmacion, pedirConfirmacion } = useConfirmacion();
  const { elemento: aviso, avisar } = useAviso();

  const esSoporte = (localStorage.getItem("CorreoElectronico") || "") === CORREO_SOPORTE;

  const capturarDatos = useCallback(async () => {
    try {
      setDatos(await cargarUsuariosTickets());
    } catch (error) {
      console.error("Error al cargar los usuarios de tickets:", error);
      avisar("No se pudieron cargar los usuarios", "error");
    } finally {
      setCargando(false);
    }
  }, [avisar]);

  useEffect(() => { capturarDatos(); }, [capturarDatos]);

  const guardar = useCallback(async (valores, usuario) => {
    try {
      if (usuario) {
        await actualizarRegistro(`${RUTA_USUARIOS}/${usuario.id}`, valores);
        avisar("Usuario actualizado");
      } else {
        await crearRegistro(RUTA_USUARIOS, valores);
        avisar("Usuario creado");
      }
      setModal(null);
      capturarDatos();
    } catch (error) {
      console.error("Error al guardar el usuario:", error);
      avisar("No se pudo guardar el usuario", "error");
    }
  }, [avisar, capturarDatos]);

  const eliminar = useCallback((usuario) => {
    pedirConfirmacion({
      titulo: "¿Eliminar usuario?",
      mensaje: (
        <>
          Se eliminará <strong>{usuario.Nombre_Completo || usuario.Usuario}</strong> de la lista de
          solicitantes. Sus tickets ya creados no se ven afectados.
        </>
      ),
      alConfirmar: async () => {
        try {
          await eliminarRegistro(`${RUTA_USUARIOS}/${usuario.id}`);
          avisar("Usuario eliminado");
          capturarDatos();
        } catch (error) {
          console.error("Error al eliminar el usuario:", error);
          avisar("No se pudo eliminar el usuario", "error");
        }
      },
    });
  }, [pedirConfirmacion, avisar, capturarDatos]);

  if (cargando) return <AnimacionCarga />;

  if (!esSoporte) {
    return (
      <div className="tk">
        <BarraSuperior />
        <div className="tk-cabecera-modulo">
          <div className="tk-vacio" style={{ padding: 60 }}>
            <i className="bi bi-lock" />
            Solo el área de soporte puede administrar los usuarios de tickets.
          </div>
        </div>
      </div>
    );
  }

  const renderizadores = {
    Nombre_Completo: (valor) => (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
        <span
          style={{
            width: 30, height: 30, borderRadius: "50%", flexShrink: 0,
            background: "linear-gradient(135deg,#e74c3c,#c0392b)", color: "#fff",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            fontSize: "0.7rem", fontWeight: 700,
          }}
        >
          {inicialesDe(valor)}
        </span>
        <strong>{valor || "(sin nombre)"}</strong>
      </span>
    ),
    Usuario: (valor) => valor || "—",
    Cargo: (valor) => valor || "—",
    Proceso: (valor) => valor || "—",
  };

  return (
    <div className="tk">
      <BarraSuperior />

      <div className="tk-cabecera-modulo">
        <div className="tk-encabezado">
          <div>
            <h1>Usuarios de tickets</h1>
            <p>{datos.length} usuario{datos.length === 1 ? "" : "s"} registrado{datos.length === 1 ? "" : "s"} como solicitantes</p>
          </div>

          <div className="tk-acciones">
            <button type="button" className="tk-btn" onClick={() => navegar("/tickets")}>
              <i className="bi bi-arrow-left" /> Tickets
            </button>
            <button type="button" className="tk-btn tk-btn-icono" title="Refrescar" onClick={capturarDatos}>
              <i className="bi bi-arrow-clockwise" />
            </button>
            <CSVLink
              data={datos}
              headers={COLUMNAS}
              filename="usuarios_tickets.csv"
              separator=";"
              className="tk-btn"
              title="Exportar a CSV"
            >
              <i className="bi bi-download" /> Exportar
            </CSVLink>
            <button type="button" className="tk-btn tk-btn-primario" onClick={() => setModal({})}>
              <i className="bi bi-plus-lg" /> Nuevo usuario
            </button>
          </div>
        </div>
      </div>

      <TablaGenerica
        datos={datos}
        titulos={COLUMNAS.map((c) => c.label.toUpperCase())}
        campos={COLUMNAS.map((c) => c.key)}
        elementosPorPagina={50}
        columnaOrdenInicial={0}
        direccionOrdenInicial="asc"
        renderizadores={renderizadores}
        mostrarBarraSuperior={false}
        botones={[
          { texto: "Editar", icono: "bi-pencil", accion: (usuario) => setModal({ usuario }) },
          { texto: "Eliminar", icono: "bi-trash", accion: eliminar },
        ]}
      />

      {modal && (
        <ModalUsuario
          usuario={modal.usuario}
          alCerrar={() => setModal(null)}
          alGuardar={(valores) => guardar(valores, modal.usuario)}
        />
      )}

      {confirmacion}
      {aviso}
    </div>
  );
});

export default ListadoUsuariosTickets;
